import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package2, ArrowLeft, RotateCcw, Globe, Mail, AlertCircle, CheckCircle, FileText } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function RefundPolicy() {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language === 'en';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <Package2 className="h-8 w-8 text-primary" />
              <span className="text-2xl font-heading font-bold">Even Rounds</span>
            </Link>
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => i18n.changeLanguage(isEnglish ? 'fi' : 'en')}
              >
                <Globe className="h-4 w-4 mr-1" />
                {isEnglish ? 'FI' : 'EN'}
              </Button>
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  {t('common.back')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <RotateCcw className="h-10 w-10 text-primary" />
            <h1 className="text-4xl font-heading font-bold">
              {isEnglish ? 'Return & Refund Policy' : 'Peruutus- ja palautusehdot'}
            </h1>
          </div>
          <p className="text-muted-foreground">
            {isEnglish ? 'Last updated: December 2025' : 'Viimeksi päivitetty: Joulukuu 2025'}
          </p>
        </div>

        {/* Important Notice */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
          <div className="flex gap-3">
            <AlertCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-amber-800 mb-2">
                {isEnglish ? 'Important: Digital Products' : 'Tärkeää: Digitaaliset tuotteet'}
              </h3>
              <p className="text-amber-700 text-sm">
                {isEnglish 
                  ? 'Even Rounds sells digital products (crochet patterns/designs). Under EU consumer law, the right of withdrawal for digital content does not apply once the download or streaming has begun with your prior consent.'
                  : 'Even Rounds myy digitaalisia tuotteita (virkkausohjeita/malleja). EU:n kuluttajansuojalain mukaan digitaalisen sisällön peruutusoikeus ei koske tilanteita, joissa lataus tai suoratoisto on alkanut etukäteen antamallasi suostumuksella.'
                }
              </p>
            </div>
          </div>
        </div>

        <div className="prose prose-stone max-w-none">
          {isEnglish ? (
            <>
              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Digital Products (Crochet Patterns)</h2>
                
                <h3 className="text-lg font-semibold mt-4">Right of Withdrawal</h3>
                <p>
                  Under the EU Consumer Rights Directive (2011/83/EU), you have 14 days to withdraw from 
                  a purchase of digital content, <strong>unless</strong>:
                </p>
                <ul>
                  <li>You have given your express consent to begin the download/access immediately</li>
                  <li>You have acknowledged that you will lose your right of withdrawal once the download begins</li>
                </ul>
                <p>
                  When you purchase a pattern on Even Rounds and click to download, you consent to 
                  immediate delivery and waive your right of withdrawal.
                </p>

                <h3 className="text-lg font-semibold mt-6">When We Do Offer Refunds</h3>
                <p>Despite the above, we will provide a full refund if:</p>
                <ul>
                  <li>
                    <CheckCircle className="inline h-4 w-4 text-green-700 mr-1" />
                    The pattern file is corrupted or unreadable
                  </li>
                  <li>
                    <CheckCircle className="inline h-4 w-4 text-green-700 mr-1" />
                    The pattern content is significantly different from what was advertised
                  </li>
                  <li>
                    <CheckCircle className="inline h-4 w-4 text-green-700 mr-1" />
                    Technical issues on our end prevented you from accessing your purchase
                  </li>
                  <li>
                    <CheckCircle className="inline h-4 w-4 text-green-700 mr-1" />
                    You were charged multiple times for the same product by mistake
                  </li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Voluntary Support/Donations</h2>
                <p>
                  Voluntary support payments (via our &quot;Support&quot; feature) are considered charitable 
                  contributions/tips and are <strong>non-refundable</strong>. These payments help us 
                  maintain the free features of Even Rounds.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Free Account Features</h2>
                <p>
                  The yarn inventory management, project tracking, and community features of Even Rounds 
                  are provided free of charge. There are no refunds applicable to free services.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">How to Request a Refund</h2>
                <p>To request a refund, please:</p>
                <ol>
                  <li>Email us at <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a></li>
                  <li>Include your order/purchase ID</li>
                  <li>Explain the reason for your refund request</li>
                  <li>Provide any relevant screenshots or documentation</li>
                </ol>
                <p>
                  We aim to respond to all refund requests within 3 business days. If approved, 
                  refunds will be processed to your original payment method within 5-10 business days.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Contact Us</h2>
                <p>
                  If you have questions about our refund policy:<br />
                  Vera Value Oy<br />
                  Email: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a>
                </p>
                <p>
                  For unresolved disputes, you may contact the Finnish Consumer Disputes Board 
                  (Kuluttajariitalautakunta) or use the EU ODR platform at{' '}
                  <a href="https://ec.europa.eu/odr" target="_blank" rel="noopener noreferrer">
                    https://ec.europa.eu/odr
                  </a>
                </p>
              </section>
            </>
          ) : (
            <>
              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Digitaaliset tuotteet (Virkkausohjeet)</h2>
                
                <h3 className="text-lg font-semibold mt-4">Peruutusoikeus</h3>
                <p>
                  EU:n kuluttajaoikeusdirektiivin (2011/83/EU) mukaan sinulla on 14 päivää aikaa 
                  peruuttaa digitaalisen sisällön osto, <strong>paitsi jos</strong>:
                </p>
                <ul>
                  <li>Olet antanut nimenomaisen suostumuksesi latauksen/pääsyn aloittamiseen välittömästi</li>
                  <li>Olet tunnustanut menettäväsi peruutusoikeutesi, kun lataus alkaa</li>
                </ul>
                <p>
                  Kun ostat ohjeen Even Roundsista ja napsautat ladataksesi, suostut välittömään 
                  toimitukseen ja luovut peruutusoikeudestasi.
                </p>

                <h3 className="text-lg font-semibold mt-6">Milloin tarjoamme hyvityksen</h3>
                <p>Edellä mainitusta huolimatta tarjoamme täyden hyvityksen, jos:</p>
                <ul>
                  <li>
                    <CheckCircle className="inline h-4 w-4 text-green-700 mr-1" />
                    Ohjetiedosto on vioittunut tai lukukelvoton
                  </li>
                  <li>
                    <CheckCircle className="inline h-4 w-4 text-green-700 mr-1" />
                    Ohjeen sisältö poikkeaa merkittävästi mainostetusta
                  </li>
                  <li>
                    <CheckCircle className="inline h-4 w-4 text-green-700 mr-1" />
                    Meidän puoleltamme johtuvat tekniset ongelmat estivät ostoksesi käytön
                  </li>
                  <li>
                    <CheckCircle className="inline h-4 w-4 text-green-700 mr-1" />
                    Sinulta veloitettiin sama tuote useaan kertaan vahingossa
                  </li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Vapaaehtoiset tukimaksut/lahjoitukset</h2>
                <p>
                  Vapaaehtoiset tukimaksut (&quot;Tue&quot;-ominaisuuden kautta) katsotaan hyväntekeväisyyslahjoituksiksi/
                  juomarahoiksi ja ovat <strong>ei-palautettavia</strong>. Nämä maksut auttavat meitä 
                  ylläpitämään Even Roundsin ilmaisia ominaisuuksia.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Ilmaiset tilin ominaisuudet</h2>
                <p>
                  Even Roundsin lankavaraston hallinta, projektiseuranta ja yhteisöominaisuudet 
                  tarjotaan maksutta. Ilmaisiin palveluihin ei sovelleta palautuksia.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Hyvityspyynnön tekeminen</h2>
                <p>Pyytääksesi hyvitystä:</p>
                <ol>
                  <li>Lähetä sähköpostia osoitteeseen <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a></li>
                  <li>Sisällytä tilaus-/ostotunnuksesi</li>
                  <li>Selitä hyvityspyyntösi syy</li>
                  <li>Liitä mukaan asiaankuuluvat kuvakaappaukset tai dokumentaatio</li>
                </ol>
                <p>
                  Pyrimme vastaamaan kaikkiin hyvityspyyntöihin 3 arkipäivän kuluessa. Jos hyväksytty, 
                  hyvitykset käsitellään alkuperäiseen maksutapaan 5-10 arkipäivän kuluessa.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-heading font-semibold">Yhteystiedot</h2>
                <p>
                  Jos sinulla on kysyttävää palautuskäytännöstämme:<br />
                  Vera Value Oy<br />
                  Sähköposti: <a href="mailto:admin@evenrounds.com">admin@evenrounds.com</a>
                </p>
                <p>
                  Ratkaisemattomissa riidoissa voit ottaa yhteyttä Kuluttajariitalautakuntaan tai 
                  käyttää EU:n ODR-alustaa osoitteessa{' '}
                  <a href="https://ec.europa.eu/odr" target="_blank" rel="noopener noreferrer">
                    https://ec.europa.eu/odr
                  </a>
                </p>
              </section>
            </>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100 bg-white mt-12">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-2 mb-2">
            <Package2 className="h-6 w-6 text-primary" />
            <span className="text-xl font-heading font-bold">Even Rounds</span>
          </div>
          <p className="text-sm text-stone-500">
            © 2026 Vera Value Oy. {isEnglish ? 'All rights reserved.' : 'Kaikki oikeudet pidätetään.'}
          </p>
        </div>
      </footer>
    </div>
  );
}
